<script>
import {useI18n} from "vue-i18n";
import {useRoute, useRouter} from "vue-router";
import {useDonationSessionStore} from "../../stores/donationSession";
import HelcimPaymentButton from "../paymentProcessors/HelcimPaymentButton.vue";
import AccordionItem from "../components/accordion/AccordionItem.vue";
import {ApplicationHelper} from "../../helpers/ApplicationHelper";
import StripePaymentButton from "../paymentProcessors/StripePaymentButton.vue";
import CurrencyInput from "../components/currencyInput/currencyInput.vue";
import BaseModal from "../components/BaseModal.vue";
import Icon from "../components/Icon.vue";
import DonationTipForm from "./DonationTipForm.vue";
import {useAnalyticEventStore} from "../../stores/analyticEvents";
import {useLandingPageStore} from "../../stores/landingPage";

export default {
  name: "DonationCheckoutSummary",
  components: {
    DonationTipForm,
    Icon,
    BaseModal,
    CurrencyInput,
    StripePaymentButton,
    AccordionItem,
    HelcimPaymentButton
  },
  props: {
    isPreview: {
      type: Boolean,
      default: false
    },

    removeFromCartEnabled: {
      type: Boolean,
      default: true
    }
  },
  setup() {
    // Stores
    const donationSessionStore = useDonationSessionStore();
    const analyticEventStore = useAnalyticEventStore();
    const landingStore = useLandingPageStore();

    // I18n
    const {t} = useI18n();

    // Router
    const router = useRouter();
    const route = useRoute();

    const defaultDonatableImage = "https://d3dwclrxdfwggc.cloudfront.net/production/customer_assets/b18991dcec9b3140236d20e8836fac0be678e55f/original/placeholder-image.png";

    return {
      t,
      router,
      route,
      donationSessionStore,
      defaultDonatableImage,
      analyticEventStore,
      landingStore
    };
  },
  computed: {
    ApplicationHelper() {
      return ApplicationHelper;
    },
    showHelcimPaymentButton() {
      return this.donationSessionStore.orgProcessorType.includes("Helcim");
    },
    showStripePaymentButton() {
      return this.donationSessionStore.orgProcessorType.includes("Stripe");
    },
  },
  methods: {
    removeFromCart(donation) {
      this.donationSessionStore.removeDonationFromCart(donation);
      this.analyticEventStore.setOrganizationId(this.donationSessionStore.organizationId);
      this.analyticEventStore.logEvent({
        action: `donation-checkout-summary-remove-from-cart`,
        traceable_type: donation.object.selectedTarget.model,
        traceable_id: donation.object.selectedTarget.id,
        metadata: {
          donation
        }
      });
    }
  },
};
</script>


<template>
  <div class="donation-checkout-summary">
    <div class="flex flex-row mb-4 mt-4">
      <i
        class="fa-solid fa-gift mr-4"
        style="font-size: 36px;"
        :style="{ color: landingStore.localBrand.color_primary_dark }"
      />
      <div
        v-if="!isPreview"
        class="flex flex-col"
      >
        <div class="title-regular font-semibold">
          Your Gifts
        </div>
        <p>Going to {{ donationSessionStore.currentOrganization.name }}</p>
      </div>
    </div>
    <div class="mb-3">
      <ul>
        <li
          v-for="(item, index) in donationSessionStore.donationCartLineItems"
          :key="index"
        >
          <div
            v-if="!isPreview"
            class="flex flex-row justify-between border border-slate-500 rounded-md p-2"
          >
            <div class="flex flex-row justify-between w-full">
              <!--  Campaign / Fund banner image or hosted placeholder -->
              <img
                v-if="item.image"
                class="object-cover w-[75px] h-[75px]"
                :src="item.image"
                :alt="item.name + ' banner image'"
              >

              <div class="pl-3 flex flex-row w-full relative text-left">
                <div class="flex flex-col justify-between w-full">
                  <div class="w-full text-xs font-bold text-gray-600 capitalize">
                    {{ item.description }}
                  </div>
                  <div
                    class="font-semibold text-lg mr-4 self-start"
                  >
                    {{ item.name }}
                  </div>

                  <div class="self-end">
                    <span class="font-bold text-lg">
                      {{ ApplicationHelper.formatMoney(item.amount) }}
                    </span>
                    <span
                      v-if="item.type === 'RecurringDonation' && item.frequency"
                      class="text-xs font-bold text-gray-600 capitalize"
                    >
                      / {{ item.frequency }}
                    </span>
                  </div>
                </div>

                <div
                  v-if="removeFromCartEnabled"
                  class="absolute top-0 right-0 cursor-pointer"
                  @click="removeFromCart(item)"
                >
                  <icon
                    icon="x"
                  />
                </div>
              </div>
            </div>
          </div>
        </li>
      </ul>
    </div>
  </div>

  <slot name="donate-button-slot" />
</template>


<style scoped>
.donation-checkout-summary {
  color: black;
}

h1 {
  font-size: 1.5em;
  margin-bottom: 20px;
}

ul {
  list-style-type: none;
  padding: 0;
}

li {
  margin-bottom: 10px;
}

strong {
  font-weight: bold;
}
</style>
